/**
 * The dispatcher's telephone, in the browser.
 *
 * The blueprint's reason for putting it here rather than on the desk: with a softphone in
 * the console the system knows who is on which call, which is what makes the pop land on
 * the right screen and a callback leave from the right extension. With handsets both are
 * guesses.
 *
 * Credentials are fetched from the API for the signed-in dispatcher and are never typed by
 * a person, never stored by this browser, and not kept past the session. They live in this
 * hook's closure and go into JsSIP; there is deliberately nowhere else to look for them.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import JsSIP from 'jssip';
import type { RTCSession } from 'jssip/lib/RTCSession';
import type { RTCSessionEvent, UA } from 'jssip/lib/UA';

import { ApiError, request, type Narrow, type Schema } from '@taxi/api-client';

import { whenPeerConnection, type PeerConnectionSource } from './media';
import { preferAlaw } from './sdp';

export type Registration =
  | { state: 'off' }
  | { state: 'connecting' }
  | { state: 'registered'; extension: string }
  | { state: 'reconnecting'; extension: string }
  | { state: 'failed'; code: string };

/** What this firm does with a caller once the order is taken. */
export type CallFlow = 'Classic' | 'HoldAndAnnounce' | 'RingBackOnly';

export type CallState =
  | { state: 'idle' }
  | { state: 'ringing'; from: string; incoming: true }
  | { state: 'calling'; to: string; incoming: false }
  | { state: 'up'; peer: string; incoming: boolean; since: number; held: boolean; muted: boolean };

type Credentials = Narrow<Schema<'Softphone'>, { callFlow: CallFlow }>;

/** Which microphone and which speaker, remembered per browser. */
export interface Devices {
  microphoneId: string | null;
  speakerId: string | null;
}

const DEVICE_KEY = 'taxi.console.audio-devices';

/**
 * A marker that survives a reload, and nothing else does.
 *
 * A page reload takes the WebSocket, the peer connection and the audio with it. There is
 * no honest way to survive that, so the next load says plainly that the call was lost
 * rather than leaving a dispatcher looking at an idle telephone wondering.
 */
const IN_CALL_KEY = 'taxi.console.call-in-progress';

export function loadDevices(): Devices {
  try {
    const stored = globalThis.localStorage?.getItem(DEVICE_KEY);

    if (stored !== null && stored !== undefined) {
      return JSON.parse(stored) as Devices;
    }
  } catch {
    // A browser with storage disabled still gets a telephone; it just forgets which
    // headset was chosen.
  }

  return { microphoneId: null, speakerId: null };
}

export function saveDevices(devices: Devices): void {
  try {
    globalThis.localStorage?.setItem(DEVICE_KEY, JSON.stringify(devices));
  } catch {
    // As above.
  }
}

/** A second caller ringing while the first is up. */
export interface WaitingCall {
  from: string;
}

/**
 * What a second caller does while a call is up.
 *
 * `show` rings and can be answered, and answering puts the first caller on hold; `busy`
 * refuses it with 486 so the PBX rings a colleague. The administrator decides, per
 * dispatcher — the ring decision already skips a busy dispatcher set to `busy`, and the
 * refusal here is the backstop for a call that was on its way when they picked up.
 */
export type SecondCalls = 'show' | 'busy';

export interface Softphone {
  registration: Registration;
  call: CallState;

  /** A second caller, ringing while `call` is up. */
  waiting: WaitingCall | null;

  /**
   * Answers the second caller.
   *
   * `holdFirst` is asked to put the first caller on hold on the PBX, and says whether it
   * did — the server moves the caller and this browser's leg of that call ends. When it
   * cannot (an outbound call is the dispatcher's own leg), the first call is held here
   * instead, in the browser, and `heldHere` says so until it is resumed.
   */
  answerWaiting: (holdFirst: () => Promise<boolean>) => Promise<void>;
  rejectWaiting: () => void;

  /** A call this browser is holding itself, because the PBX could not take it. */
  heldHere: { peer: string } | null;
  resumeHeld: () => void;

  /** Whether a second caller rings or is refused. */
  setSecondCalls: (policy: SecondCalls) => void;

  /**
   * What this firm does with a caller once the order is taken.
   *
   * Learned from the credentials rather than from the settings screen, which is
   * admin-only — a dispatcher may not read it, and this is what decides whether they are
   * offered the "waiting for a car" button at all.
   */
  callFlow: CallFlow;

  /** True when the previous page load ended with a call in progress. */
  lostOnReload: boolean;
  acknowledgeLoss: () => void;

  answer: () => void;

  /**
   * True when the last answer did not result in a call.
   *
   * Almost always a microphone: refused, or absent. It matters because taking a call
   * answers it, so the dispatcher is looking at a filled order form and has every reason
   * to believe they are connected — while the caller hears ringing.
   */
  answerFailed: boolean;
  acknowledgeAnswerFailure: () => void;
  hangUp: () => void;
  dial: (destination: string) => void;
  hold: (held: boolean) => void;
  mute: (muted: boolean) => void;
  transfer: (destination: string) => void;
  dtmf: (tone: string) => void;

  devices: Devices;
  chooseDevices: (devices: Devices) => void;

  /** The element the far end's audio is played through. Attach it to an <audio>. */
  audio: React.RefObject<HTMLAudioElement | null>;
}

export function useSoftphone(): Softphone {
  const [registration, setRegistration] = useState<Registration>({ state: 'off' });
  const [call, setCall] = useState<CallState>({ state: 'idle' });
  const [answerFailed, setAnswerFailed] = useState(false);
  /** Set while an answer is in flight, so a failure can be told from an ordinary hangup. */
  const answering = useRef(false);

  // Classic until the credentials say otherwise, which is the safe default: the button
  // that parks a caller is the one thing here that would refuse if the firm does not
  // park anybody.
  const [callFlow, setCallFlow] = useState<CallFlow>('Classic');
  const [devices, setDevices] = useState<Devices>(loadDevices);
  const [lostOnReload, setLostOnReload] = useState(
    () => globalThis.sessionStorage?.getItem(IN_CALL_KEY) === 'yes',
  );

  const agent = useRef<UA | null>(null);
  const session = useRef<RTCSession | null>(null);

  /** A second caller ringing, and the call held here to take it. */
  const waitingSession = useRef<RTCSession | null>(null);
  const heldSession = useRef<RTCSession | null>(null);
  const secondCalls = useRef<SecondCalls>('show');

  const [waiting, setWaiting] = useState<WaitingCall | null>(null);
  const [heldHere, setHeldHere] = useState<{ peer: string } | null>(null);
  const audio = useRef<HTMLAudioElement | null>(null);
  const chosen = useRef<Devices>(devices);

  // Mirrored into a ref rather than read from state, because the JsSIP callbacks that
  // need it are registered once and would otherwise capture whichever headset was chosen
  // when the call started. Written in an effect: mutating a ref during a render React
  // then throws away leaves it pointing at a value that never applied.
  useEffect(() => {
    chosen.current = devices;
  }, [devices]);

  const noteInCall = useCallback((inCall: boolean) => {
    try {
      if (inCall) globalThis.sessionStorage?.setItem(IN_CALL_KEY, 'yes');
      else globalThis.sessionStorage?.removeItem(IN_CALL_KEY);
    } catch {
      // Storage refused. The reload message is a courtesy, not a feature.
    }
  }, []);

  /** What this browser should capture and play through. */
  const constraints = useCallback(
    () => ({
      audio: chosen.current.microphoneId === null
        ? true
        : { deviceId: { exact: chosen.current.microphoneId } },
      video: false,
    }),
    [],
  );

  const attach = useCallback((rtc: RTCSession) => {
    // Not `rtc.connection` directly: an incoming session has none until it is answered,
    // and reading through it throws before JsSIP has sent its 180 Ringing — so the
    // telephone never rings and no Answer button is ever drawn. media.ts has the whole
    // story, and a test.
    whenPeerConnection(rtc as unknown as PeerConnectionSource<RTCPeerConnection>, (connection) => {
      connection.addEventListener('track', (event) => {
        const element = audio.current;

        if (element === null) return;

        element.srcObject = event.streams[0];

        // Chosen speaker, where the browser allows it. setSinkId is Chromium-only, and the
        // console is a Chrome or Edge application by the hardware doc's own recommendation.
        const speaker = chosen.current.speakerId;
        const withSink = element as HTMLAudioElement & {
          setSinkId?: (id: string) => Promise<void>;
        };

        if (speaker !== null && typeof withSink.setSinkId === 'function') {
          void withSink.setSinkId(speaker).catch(() => {
            // A headset that has been unplugged since it was chosen. Default output is a
            // better answer than no audio.
          });
        }

        void element.play().catch(() => {
          // Autoplay refused because nothing has been clicked yet. The next interaction
          // starts it, and answering a call is an interaction.
        });
      });
    });

    // alaw ahead of Opus, so the PBX forwards packets instead of transcoding every call.
    rtc.on('sdp', (event) => {
      event.sdp = preferAlaw(event.sdp);
    });
  }, []);

  const wire = useCallback(
    (rtc: RTCSession, peer: string, incoming: boolean) => {
      session.current = rtc;
      attach(rtc);

      rtc.on('progress', () => {
        if (!incoming) setCall({ state: 'calling', to: peer, incoming: false });
      });

      rtc.on('confirmed', () => {
        answering.current = false;
        noteInCall(true);
        setCall({
          state: 'up',
          peer,
          incoming,
          since: Date.now(),
          held: false,
          muted: false,
        });
      });

      // Only the current call's end resets the telephone. With a second call answered, the
      // first one's end — the PBX taking it away to hold, or a caller held here hanging up —
      // must not blank out the call the dispatcher is now talking to.
      const finished = () => {
        if (heldSession.current === rtc) {
          heldSession.current = null;
          setHeldHere(null);
          return;
        }

        if (session.current !== rtc) return;

        session.current = null;
        noteInCall(false);
        setCall({ state: 'idle' });
      };

      rtc.on('ended', finished);
      rtc.on('failed', () => {
        // A call that failed while we were answering it never became a call. Anything else
        // is an ordinary end -- the caller hung up, or the far end refused.
        if (answering.current) setAnswerFailed(true);
        answering.current = false;
        finished();
      });

      const current = (update: (c: CallState) => CallState) => {
        if (session.current === rtc) setCall(update);
      };

      rtc.on('hold', () => current((c) => (c.state === 'up' ? { ...c, held: true } : c)));
      rtc.on('unhold', () => current((c) => (c.state === 'up' ? { ...c, held: false } : c)));
      rtc.on('muted', () => current((c) => (c.state === 'up' ? { ...c, muted: true } : c)));
      rtc.on('unmuted', () => current((c) => (c.state === 'up' ? { ...c, muted: false } : c)));
    },
    [attach, noteInCall],
  );

  useEffect(() => {
    let cancelled = false;
    let ua: UA | null = null;

    void (async () => {
      let credentials: Credentials;

      setRegistration({ state: 'connecting' });

      try {
        credentials = await request<Credentials>('/api/telephony/softphone', { method: 'POST' });
      } catch (failure) {
        if (cancelled) return;

        // A firm with no telephone system is the ordinary case for most of this project's
        // life, and it is not a failure worth shouting about. The console simply has no
        // telephone in it.
        setRegistration({
          state: 'failed',
          code: failure instanceof ApiError ? failure.problem.code : 'telephony.pbx_unavailable',
        });
        return;
      }

      // Before the WebSocket check, deliberately. A firm can run the ring-back-only flow
      // with no browser telephone at all, and the panel that says so still needs to know
      // which flow it is.
      setCallFlow(credentials.callFlow);

      if (cancelled || credentials.webSocketUrl === '') {
        setRegistration({ state: 'failed', code: 'telephony.not_enabled' });
        return;
      }

      ua = new JsSIP.UA({
        sockets: [new JsSIP.WebSocketInterface(credentials.webSocketUrl)],
        uri: `sip:${credentials.extension}@${credentials.domain}`,
        password: credentials.password,
        authorization_user: credentials.extension,
        display_name: credentials.displayName ?? undefined,
        register: true,
        session_timers: false,
      });

      agent.current = ua;

      ua.on('registered', () =>
        setRegistration({ state: 'registered', extension: credentials.extension }),
      );

      ua.on('disconnected', () =>
        setRegistration((current) =>
          current.state === 'registered'
            ? { state: 'reconnecting', extension: credentials.extension }
            : current,
        ),
      );

      ua.on('registrationFailed', (event) =>
        setRegistration({
          state: 'failed',
          code: event.cause === 'Rejected' ? 'telephony.pbx_unauthorised' : 'telephony.pbx_unavailable',
        }),
      );

      // The union of the incoming and outgoing shapes, which JsSIP declares as a union
      // of two *listeners* rather than a listener of a union — so the parameter has to be
      // named rather than inferred.
      ua.on('newRTCSession', (event: RTCSessionEvent) => {
        if (event.originator !== 'remote') return;

        // A second caller while one is up. Shown and answerable unless the administrator
        // set this dispatcher to pass second calls on; then refused, so the caller hears
        // busy only if nobody else is free and the PBX rings a colleague otherwise. One
        // waiting at a time — a third is refused either way, because a handset that can
        // hold only two is what the rest of the office is built around.
        if (session.current !== null) {
          if (secondCalls.current === 'busy' || waitingSession.current !== null) {
            event.session.terminate({ status_code: 486 });
            return;
          }

          const second = event.session;

          waitingSession.current = second;
          setWaiting({ from: event.request.from.uri.user });

          const gone = () => {
            if (waitingSession.current !== second) return;
            waitingSession.current = null;
            setWaiting(null);
          };

          second.on('ended', gone);
          second.on('failed', gone);
          return;
        }

        const from = event.request.from.uri.user;

        wire(event.session, from, true);
        setCall({ state: 'ringing', from, incoming: true });
      });

      ua.start();
    })();

    return () => {
      cancelled = true;

      session.current?.terminate();
      session.current = null;
      waitingSession.current?.terminate();
      waitingSession.current = null;
      heldSession.current?.terminate();
      heldSession.current = null;

      ua?.stop();
      agent.current = null;
    };
  }, [wire]);

  const answer = useCallback(() => {
    if (session.current === null) return;

    answering.current = true;
    setAnswerFailed(false);
    session.current.answer({ mediaConstraints: constraints(), pcConfig: { iceServers: [] } });
  }, [constraints]);

  const acknowledgeAnswerFailure = useCallback(() => setAnswerFailed(false), []);

  const answerWaiting = useCallback(
    async (holdFirst: () => Promise<boolean>) => {
      const second = waitingSession.current;

      if (second === null) return;

      const first = session.current;
      const peer = call.state === 'up' ? call.peer : '';

      if (first !== null) {
        // The PBX first: it frees this telephone entirely, and the caller hears the firm's
        // music rather than silence. Only a call it cannot take is held here instead.
        const parked = await holdFirst().catch(() => false);

        if (!parked) {
          first.hold();
          heldSession.current = first;
          setHeldHere({ peer });
        }
      }

      // The first call is somebody else's business now — ended by the PBX, or held here —
      // so its events must not reach the state the second call is about to take over.
      session.current = null;
      waitingSession.current = null;
      setWaiting(null);

      const from = second.remote_identity.uri.user;

      wire(second, from, true);
      answering.current = true;
      setAnswerFailed(false);
      second.answer({ mediaConstraints: constraints(), pcConfig: { iceServers: [] } });
    },
    [call, constraints, wire],
  );

  const rejectWaiting = useCallback(() => {
    waitingSession.current?.terminate({ status_code: 486 });
  }, []);

  const resumeHeld = useCallback(() => {
    const held = heldSession.current;

    // Only onto an empty line: resuming over a live call would put two callers in one ear.
    if (held === null || session.current !== null) return;

    heldSession.current = null;
    setHeldHere(null);
    session.current = held;
    held.unhold();

    // Its audio again. The element has been playing the second call since it was answered.
    const element = audio.current;
    const connection = (held as unknown as { connection: RTCPeerConnection | null }).connection;

    if (element !== null && connection !== null) {
      element.srcObject = new MediaStream(
        connection.getReceivers().map((receiver) => receiver.track),
      );
      void element.play().catch(() => undefined);
    }

    noteInCall(true);
    setCall({
      state: 'up',
      peer: held.remote_identity.uri.user,
      incoming: held.direction === 'incoming',
      since: Date.now(),
      held: false,
      muted: false,
    });
  }, [noteInCall]);

  const setSecondCalls = useCallback((policy: SecondCalls) => {
    secondCalls.current = policy;
  }, []);

  const hangUp = useCallback(() => {
    session.current?.terminate();
  }, []);

  const dial = useCallback(
    (destination: string) => {
      const ua = agent.current;

      if (ua === null || session.current !== null || destination.trim() === '') return;

      const target = destination.trim();
      const rtc = ua.call(target, {
        mediaConstraints: constraints(),
        pcConfig: { iceServers: [] },
      });

      wire(rtc, target, false);
      setCall({ state: 'calling', to: target, incoming: false });
    },
    [constraints, wire],
  );

  const hold = useCallback((held: boolean) => {
    if (held) session.current?.hold();
    else session.current?.unhold();
  }, []);

  const mute = useCallback((muted: boolean) => {
    if (muted) session.current?.mute({ audio: true });
    else session.current?.unmute({ audio: true });
  }, []);

  const transfer = useCallback((destination: string) => {
    // Blind: the call goes somewhere else and this dispatcher steps out of it. An
    // attended transfer — talk to the colleague first, then join them — is a different
    // feature and nobody has asked for it.
    if (destination.trim() !== '') session.current?.refer(destination.trim());
  }, []);

  const dtmf = useCallback((tone: string) => {
    session.current?.sendDTMF(tone);
  }, []);

  const chooseDevices = useCallback((next: Devices) => {
    setDevices(next);
    saveDevices(next);
  }, []);

  const acknowledgeLoss = useCallback(() => {
    setLostOnReload(false);
    try {
      globalThis.sessionStorage?.removeItem(IN_CALL_KEY);
    } catch {
      // As above.
    }
  }, []);

  return {
    registration,
    call,
    waiting,
    answerWaiting,
    rejectWaiting,
    heldHere,
    resumeHeld,
    setSecondCalls,
    callFlow,
    lostOnReload,
    acknowledgeLoss,
    answer,
    answerFailed,
    acknowledgeAnswerFailure,
    hangUp,
    dial,
    hold,
    mute,
    transfer,
    dtmf,
    devices,
    chooseDevices,
    audio,
  };
}
